import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import {
  computeDatetimePickerPanelStyle,
  EVENTQ_DATETIME_PICKER_ESTIMATED_PANEL_HEIGHT_PX,
  resolveDatetimePickerPlacement,
  type EventqDatetimePickerPanelStyle,
  type EventqDatetimePickerResolvedPlacement,
} from './datetime-picker-position';
import {
  buildCalendarWeeks,
  clampDateToRange,
  clampTimeUnit,
  EVENTQ_WEEKDAY_LABELS,
  formatEventDateDisplay,
  formatEventDateTimeDisplay,
  formatMonthYearLabel,
  getZonedParts,
  mergeDateAndTime,
  mergeZonedDateAndTime,
  padTimeUnit,
  zonedPartsToUtcDate,
} from './datetime-picker.utils';

export type EventqDatetimePickerMode = 'inline' | 'field';
type EventqTimeSegment = 'hours' | 'minutes' | 'seconds';

const TIME_SEGMENT_MAX: Record<EventqTimeSegment, number> = {
  hours: 23,
  minutes: 59,
  seconds: 59,
};
export type EventqDatetimePickerPanelPlacement = 'above' | 'below';
export type EventqDatetimePickerPanelAlign = 'start' | 'end';

@Component({
  selector: 'eventq-datetime-picker',
  imports: [MatIcon],
  templateUrl: './datetime-picker.html',
  styleUrl: './datetime-picker.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[attr.disabled]': 'disabled() ? "" : null',
  },
})
export class EventqDatetimePicker {
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly destroyRef = inject(DestroyRef);
  private readonly panel = viewChild<ElementRef<HTMLElement>>('panel');
  private positionFrame: number | null = null;
  private panelHome: { parent: Node; next: ChildNode | null } | null = null;

  readonly value = input<Date | null>(null);
  readonly mode = input<EventqDatetimePickerMode>('inline');
  readonly showSeconds = input(true);
  /** When true, hides time controls and formats the field as a calendar date only. */
  readonly dateOnly = input(false);
  /** When false, hides Cancel/Apply and commits on each calendar/time change. */
  readonly showActions = input(true);
  readonly disabled = input(false);
  readonly fieldLabel = input('Playback time');
  /** Closed-field timezone suffix. Blank keeps the default `CT` unless an IANA zone is set. */
  readonly timeZoneAbbreviation = input('');
  /**
   * IANA zone for display and editing (e.g. `America/Chicago`).
   * Blank keeps the host local zone. Invalid values fall back to UTC.
   */
  readonly timeZone = input('');
  /**
   * Same as `timeZone`. Prefer this in host templates — `[timeZone]` can fail to
   * bind across compilation units.
   */
  readonly ianaTimeZone = input('');
  /** Shown when `value` is null in field mode. */
  readonly fieldPlaceholder = input('Select');
  /** Renders a red required asterisk after the floating field label. */
  readonly required = input(false);
  /**
   * Preferred field-mode popover placement. The panel auto-flips when the
   * preferred side would overflow the viewport.
   */
  readonly panelPlacement = input<EventqDatetimePickerPanelPlacement>('above');
  readonly panelAlign = input<EventqDatetimePickerPanelAlign>('end');
  /** Inclusive lower bound for selectable date/time. */
  readonly min = input<Date | null>(null);
  /** Inclusive upper bound for selectable date/time. */
  readonly max = input<Date | null>(null);

  readonly applied = output<Date>();
  readonly cancelled = output<void>();

  readonly panelOpen = signal(false);
  readonly panelStyle = signal<Partial<EventqDatetimePickerPanelStyle>>({});
  readonly resolvedPlacement = signal<EventqDatetimePickerResolvedPlacement>('above');
  readonly viewMonth = signal(new Date());
  readonly draftDate = signal<Date>(new Date());
  readonly draftHours = signal(new Date().getHours());
  readonly draftMinutes = signal(new Date().getMinutes());
  readonly draftSeconds = signal(new Date().getSeconds());

  readonly weekdayLabels = EVENTQ_WEEKDAY_LABELS;
  readonly resolvedTimeZone = computed((): string | null => {
    const raw = this.ianaTimeZone().trim() || this.timeZone().trim();
    if (!raw) {
      return null;
    }
    try {
      new Intl.DateTimeFormat('en-US', { timeZone: raw }).format(new Date());
      return raw;
    } catch {
      return 'UTC';
    }
  });
  readonly monthLabel = computed(() => formatMonthYearLabel(this.viewMonth()));
  readonly calendarWeeks = computed(() =>
    buildCalendarWeeks(
      this.viewMonth(),
      this.draftDate(),
      this.min(),
      this.max(),
      this.resolvedTimeZone(),
    ),
  );
  readonly fieldDisplay = computed(() => {
    const current = this.value();
    if (!current) {
      return this.fieldPlaceholder();
    }
    const timeZone = this.resolvedTimeZone();
    return this.dateOnly()
      ? formatEventDateDisplay(current, timeZone)
      : formatEventDateTimeDisplay(current, this.timeZoneAbbreviation(), timeZone);
  });
  readonly hasValue = computed(() => this.value() !== null);

  /** Pending selection: the calendar day merged with the time steppers. */
  readonly draftDateTime = computed(() => {
    const timeZone = this.resolvedTimeZone();
    if (timeZone) {
      return mergeZonedDateAndTime(
        this.draftDate(),
        this.draftHours(),
        this.draftMinutes(),
        this.draftSeconds(),
        timeZone,
      );
    }
    return mergeDateAndTime(
      this.draftDate(),
      this.draftHours(),
      this.draftMinutes(),
      this.draftSeconds(),
    );
  });

  /** Apply stays disabled while the draft sits outside `[min, max]`. */
  readonly canApply = computed(() => {
    const draft = this.draftDateTime().getTime();
    const min = this.min();
    const max = this.max();
    if (min && draft < min.getTime()) {
      return false;
    }
    return !(max && draft > max.getTime());
  });

  private readonly editingSegment = signal<EventqTimeSegment | null>(null);
  private readonly editBuffer = signal('');
  private focusValue = 0;

  readonly hoursField = computed(() =>
    this.editingSegment() === 'hours' ? this.editBuffer() : padTimeUnit(this.draftHours()),
  );
  readonly minutesField = computed(() =>
    this.editingSegment() === 'minutes' ? this.editBuffer() : padTimeUnit(this.draftMinutes()),
  );
  readonly secondsField = computed(() =>
    this.editingSegment() === 'seconds' ? this.editBuffer() : padTimeUnit(this.draftSeconds()),
  );

  readonly canGoPreviousMonth = computed(() => {
    const min = this.min();
    if (!min) {
      return true;
    }
    const timeZone = this.resolvedTimeZone();
    const view = this.viewMonth();
    if (timeZone) {
      const prev = new Date(view.getFullYear(), view.getMonth(), 0);
      const prevMonthEnd = zonedPartsToUtcDate(
        {
          year: prev.getFullYear(),
          month: prev.getMonth() + 1,
          day: prev.getDate(),
          hours: 23,
          minutes: 59,
          seconds: 59,
        },
        timeZone,
      );
      return prevMonthEnd.getTime() >= min.getTime();
    }
    const prevMonthEnd = new Date(view.getFullYear(), view.getMonth(), 0);
    prevMonthEnd.setHours(23, 59, 59, 999);
    return prevMonthEnd.getTime() >= min.getTime();
  });

  readonly canGoNextMonth = computed(() => {
    const max = this.max();
    if (!max) {
      return true;
    }
    const timeZone = this.resolvedTimeZone();
    const view = this.viewMonth();
    if (timeZone) {
      const nextMonthStart = zonedPartsToUtcDate(
        {
          year: view.getMonth() === 11 ? view.getFullYear() + 1 : view.getFullYear(),
          month: view.getMonth() === 11 ? 1 : view.getMonth() + 2,
          day: 1,
          hours: 0,
          minutes: 0,
          seconds: 0,
        },
        timeZone,
      );
      return nextMonthStart.getTime() <= max.getTime();
    }
    const nextMonthStart = new Date(view.getFullYear(), view.getMonth() + 1, 1);
    nextMonthStart.setHours(0, 0, 0, 0);
    return nextMonthStart.getTime() <= max.getTime();
  });

  constructor() {
    // Capture so a click still counts when the target stops bubbling, as the
    // grid layout select does.
    const onDocumentClick = (event: MouseEvent): void => {
      this.closeFieldPanelOnOutsideClick(event);
    };
    document.addEventListener('click', onDocumentClick, true);
    this.destroyRef.onDestroy(() => {
      document.removeEventListener('click', onDocumentClick, true);
    });

    effect(() => {
      const current = this.value();
      this.resolvedTimeZone();
      if (!current) {
        return;
      }
      // Bounds are read untracked: a moving `max` (such as "now") must not
      // discard the draft the user is still editing.
      const [min, max] = untracked(() => [this.min(), this.max()] as const);
      this.syncDraftFromDate(clampDateToRange(current, min, max));
    });

    effect(() => {
      this.panelOpen();
      this.panelPlacement();
      this.panelAlign();
      this.mode();
      this.schedulePanelPosition();
    });

    effect(() => {
      const open = this.panelOpen();
      const fieldMode = this.mode() === 'field';
      const panelEl = this.panel()?.nativeElement;
      if (!panelEl) {
        return;
      }
      if (open && fieldMode) {
        this.portalPanel(panelEl);
      } else {
        this.restorePanel();
      }
    });

    afterNextRender(() => {
      this.updatePanelPosition();
      const onScroll = (): void => this.schedulePanelPosition();
      document.addEventListener('scroll', onScroll, true);
      this.destroyRef.onDestroy(() => {
        this.clearScheduledPosition();
        this.restorePanel();
        document.removeEventListener('scroll', onScroll, true);
      });
    });
  }

  @HostListener('window:resize')
  onViewportChange(): void {
    this.schedulePanelPosition();
  }

  /**
   * Field popovers close on a click outside this field and its own panel.
   * The draft is dropped by not applying it. Inline calendars stay put.
   */
  private closeFieldPanelOnOutsideClick(event: MouseEvent): void {
    if (this.mode() !== 'field' || !this.panelOpen()) {
      return;
    }
    const target = event.target;
    if (!(target instanceof Node)) {
      return;
    }
    if (this.host.nativeElement.contains(target)) {
      return;
    }
    const panelEl = this.panel()?.nativeElement;
    if (panelEl?.contains(target)) {
      return;
    }
    this.panelOpen.set(false);
  }

  onTogglePanel(): void {
    if (this.disabled()) {
      return;
    }
    if (!this.panelOpen()) {
      const seed = clampDateToRange(this.value() ?? new Date(), this.min(), this.max());
      this.syncDraftFromDate(seed);
    }
    this.panelOpen.update((open) => !open);
  }

  onPreviousMonth(): void {
    if (!this.canGoPreviousMonth()) {
      return;
    }
    const next = new Date(this.viewMonth());
    next.setMonth(next.getMonth() - 1);
    this.viewMonth.set(next);
  }

  onNextMonth(): void {
    if (!this.canGoNextMonth()) {
      return;
    }
    const next = new Date(this.viewMonth());
    next.setMonth(next.getMonth() + 1);
    this.viewMonth.set(next);
  }

  onSelectDay(cellDate: Date, disabled: boolean): void {
    if (disabled) {
      return;
    }
    this.draftDate.set(new Date(cellDate));
    this.commitIfActionsHidden();
  }

  onStepHours(delta: number, event?: Event): void {
    event?.stopPropagation();
    if (this.disabled()) {
      return;
    }
    this.abandonSegmentEdit('hours');
    this.draftHours.set(clampTimeUnit(this.draftHours() + delta, 23));
    this.commitIfActionsHidden();
  }

  onStepMinutes(delta: number, event?: Event): void {
    event?.stopPropagation();
    if (this.disabled()) {
      return;
    }
    this.abandonSegmentEdit('minutes');
    this.draftMinutes.set(clampTimeUnit(this.draftMinutes() + delta, 59));
    this.commitIfActionsHidden();
  }

  onStepSeconds(delta: number, event?: Event): void {
    event?.stopPropagation();
    if (this.disabled()) {
      return;
    }
    this.abandonSegmentEdit('seconds');
    this.draftSeconds.set(clampTimeUnit(this.draftSeconds() + delta, 59));
    this.commitIfActionsHidden();
  }

  onTimeFocus(segment: EventqTimeSegment): void {
    if (this.disabled() || this.editingSegment() === segment) {
      return;
    }
    this.editingSegment.set(segment);
    this.focusValue = this.readSegment(segment);
    this.editBuffer.set(padTimeUnit(this.focusValue));
  }

  onTimeKeydown(segment: EventqTimeSegment, event: KeyboardEvent): void {
    if (this.disabled() || event.key === 'Tab') {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (this.editingSegment() !== segment) {
      this.onTimeFocus(segment);
    }
    if (event.key === 'Backspace') {
      this.editBuffer.update((current) => current.slice(0, -1));
      this.syncDraftFromBuffer(segment);
      return;
    }
    if (/^[0-9]$/.test(event.key)) {
      this.shiftInDigit(segment, Number(event.key));
    }
  }

  onTimePaste(segment: EventqTimeSegment, event: ClipboardEvent): void {
    if (this.disabled()) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (this.editingSegment() !== segment) {
      this.onTimeFocus(segment);
    }
    const text = event.clipboardData?.getData('text') ?? '';
    for (const char of text) {
      if (/^[0-9]$/.test(char)) {
        this.shiftInDigit(segment, Number(char));
      }
    }
  }

  onTimeBlur(segment: EventqTimeSegment): void {
    if (this.disabled() || this.editingSegment() !== segment) {
      return;
    }
    const raw = this.editBuffer().trim();
    if (raw === '') {
      this.writeSegment(segment, this.focusValue);
    } else {
      const parsed = Number(raw);
      const max = TIME_SEGMENT_MAX[segment];
      const clamped = Math.min(Math.max(Number.isFinite(parsed) ? parsed : this.focusValue, 0), max);
      this.writeSegment(segment, clamped);
    }
    this.editingSegment.set(null);
    this.commitIfActionsHidden();
  }

  onCancel(): void {
    this.panelOpen.set(false);
    this.cancelled.emit();
  }

  /** Closes a field calendar without applying the draft. */
  dismiss(): void {
    if (this.mode() !== 'field' || !this.panelOpen()) {
      return;
    }
    this.panelOpen.set(false);
  }

  onApply(): void {
    if (!this.canApply()) {
      return;
    }
    this.panelOpen.set(false);
    this.commitDraft();
  }

  private schedulePanelPosition(): void {
    this.clearScheduledPosition();
    this.positionFrame = requestAnimationFrame(() => {
      this.positionFrame = null;
      this.updatePanelPosition();
    });
  }

  private clearScheduledPosition(): void {
    if (this.positionFrame !== null) {
      cancelAnimationFrame(this.positionFrame);
      this.positionFrame = null;
    }
  }

  private updatePanelPosition(): void {
    if (this.mode() !== 'field' || !this.panelOpen()) {
      this.panelStyle.set({});
      return;
    }

    const field = this.host.nativeElement.querySelector(
      '.eventq-datetime-picker__field',
    ) as HTMLElement | null;
    if (!field) {
      this.panelStyle.set({});
      return;
    }

    const panelEl = this.panel()?.nativeElement;
    if (panelEl && this.mode() === 'field') {
      this.portalPanel(panelEl);
    }

    const fieldRect = field.getBoundingClientRect();
    const measuredHeight =
      panelEl?.offsetHeight || EVENTQ_DATETIME_PICKER_ESTIMATED_PANEL_HEIGHT_PX;
    const measuredWidth = panelEl?.offsetWidth || undefined;
    const preferred = this.panelPlacement();
    this.resolvedPlacement.set(
      resolveDatetimePickerPlacement(
        preferred,
        fieldRect,
        measuredHeight,
        window.innerHeight,
      ),
    );

    this.panelStyle.set(
      computeDatetimePickerPanelStyle(
        fieldRect,
        preferred,
        this.panelAlign(),
        window.innerWidth,
        window.innerHeight,
        measuredHeight,
        measuredWidth,
      ),
    );
  }

  private portalPanel(panel: HTMLElement): void {
    if (panel.parentElement === document.body) {
      return;
    }
    this.panelHome = {
      parent: panel.parentNode as Node,
      next: panel.nextSibling,
    };
    document.body.appendChild(panel);
  }

  private restorePanel(): void {
    const panel = this.panel()?.nativeElement;
    if (!panel || !this.panelHome) {
      this.panelHome = null;
      return;
    }

    if (this.panelHome.next?.parentNode === this.panelHome.parent) {
      this.panelHome.parent.insertBefore(panel, this.panelHome.next);
    } else {
      this.panelHome.parent.appendChild(panel);
    }
    this.panelHome = null;
  }

  private abandonSegmentEdit(segment: EventqTimeSegment): void {
    if (this.editingSegment() === segment) {
      this.editingSegment.set(null);
    }
  }

  private readSegment(segment: EventqTimeSegment): number {
    if (segment === 'hours') {
      return this.draftHours();
    }
    if (segment === 'minutes') {
      return this.draftMinutes();
    }
    return this.draftSeconds();
  }

  private writeSegment(segment: EventqTimeSegment, value: number): void {
    if (segment === 'hours') {
      this.draftHours.set(value);
      return;
    }
    if (segment === 'minutes') {
      this.draftMinutes.set(value);
      return;
    }
    this.draftSeconds.set(value);
  }

  private shiftInDigit(segment: EventqTimeSegment, digit: number): void {
    const current = this.editBuffer() === '' ? 0 : Number(this.editBuffer());
    const shifted = (current % 10) * 10 + digit;
    this.editBuffer.set(padTimeUnit(shifted));
    this.syncDraftFromBuffer(segment);
  }

  private syncDraftFromBuffer(segment: EventqTimeSegment): void {
    const raw = this.editBuffer();
    if (raw === '') {
      return;
    }
    const parsed = Number(raw);
    if (!Number.isFinite(parsed) || parsed > TIME_SEGMENT_MAX[segment]) {
      return;
    }
    this.writeSegment(segment, parsed);
  }

  private commitIfActionsHidden(): void {
    if (!this.showActions() && this.canApply()) {
      this.commitDraft();
    }
  }

  private commitDraft(): void {
    const applied = clampDateToRange(this.draftDateTime(), this.min(), this.max());
    this.syncDraftFromDate(applied);
    this.applied.emit(applied);
  }

  private syncDraftFromDate(date: Date): void {
    const timeZone = this.resolvedTimeZone();
    if (timeZone) {
      const parts = getZonedParts(date, timeZone);
      this.viewMonth.set(new Date(parts.year, parts.month - 1, 1));
      this.draftDate.set(
        zonedPartsToUtcDate({ ...parts, hours: 12, minutes: 0, seconds: 0 }, timeZone),
      );
      this.draftHours.set(parts.hours);
      this.draftMinutes.set(parts.minutes);
      this.draftSeconds.set(parts.seconds);
      return;
    }
    this.viewMonth.set(new Date(date.getFullYear(), date.getMonth(), 1));
    this.draftDate.set(new Date(date));
    this.draftHours.set(date.getHours());
    this.draftMinutes.set(date.getMinutes());
    this.draftSeconds.set(date.getSeconds());
  }
}
